const ACUITY_EMBED_SCRIPT = "https://embed.acuityscheduling.com/js/embed.js";

// Acuity sometimes renders its own business-logo inside the iframe and sometimes
// not. We always render our own logo as a fallback, then hide it whenever Acuity's
// logo is actually on screen so the two don't show at once.
const ACUITY_LOGO_SELECTOR = "img[src*='logo'], img[src*='acuityscheduling']";

let acuityLoaded = false;
let savedScrollY = 0;

const isElementVisible = (el: HTMLElement): boolean => {
  const rect = el.getBoundingClientRect();
  return el.offsetParent !== null && rect.width > 0 && rect.height > 0;
};

const isAcuityLogoVisible = (doc: Document): boolean =>
  Array.from(doc.querySelectorAll<HTMLImageElement>(ACUITY_LOGO_SELECTOR)).some(
    isElementVisible,
  );

// Toggle our fallback logo based on whether Acuity's own logo is rendered inside
// the iframe. The iframe is served from app.acuityscheduling.com (cross-origin),
// so reading its document throws a SecurityError — in that case we can't inspect
// it and leave our fallback logo visible.
const syncBusinessLogo = (iframe: HTMLIFrameElement, embed: HTMLElement) => {
  let doc: Document | null = null;
  try {
    doc = iframe.contentDocument ?? iframe.contentWindow?.document ?? null;
  } catch {
    return;
  }

  if (!doc) {
    return;
  }

  const iframeDoc = doc;

  // Acuity wraps its (often empty) business-logo header in padding that leaves a
  // ~52px gap. Strip it at the source when the iframe is same-origin readable;
  // cross-origin loads fall back to the negative margin-top collapse in CSS.
  const style = iframeDoc.createElement("style");
  style.textContent =
    "#pylon header > div:first-child { padding: 0 !important; }";
  iframeDoc.head?.appendChild(style);

  const update = () =>
    embed.classList.toggle(
      "acuity-embed--has-acuity-logo",
      isAcuityLogoVisible(iframeDoc),
    );

  update();

  const observer = new MutationObserver(update);
  observer.observe(iframeDoc.documentElement, {
    childList: true,
    subtree: true,
  });
};

const lockBodyScroll = () => {
  savedScrollY = window.scrollY;
  const { style } = document.body;
  style.position = "fixed";
  style.top = `-${savedScrollY}px`;
  style.left = "0";
  style.right = "0";
  style.width = "100%";
};

const unlockBodyScroll = () => {
  const { style } = document.body;
  style.position = "";
  style.top = "";
  style.left = "";
  style.right = "";
  style.width = "";
  window.scrollTo(0, savedScrollY);
};

const loadAcuityEmbed = (popup: HTMLElement) => {
  if (acuityLoaded) {
    return;
  }
  acuityLoaded = true;

  const iframe = popup.querySelector<HTMLIFrameElement>(
    "[data-acuity-embed] .acuity-embed__iframe",
  );

  if (iframe) {
    const src = iframe.dataset.acuitySrc;
    const embed = iframe.closest<HTMLElement>("[data-acuity-embed]");

    if (embed) {
      iframe.addEventListener(
        "load",
        () => {
          embed.classList.add("acuity-embed--loaded");
          syncBusinessLogo(iframe, embed);
        },
        { once: true },
      );
    }

    if (src) {
      iframe.src = src;
    }
  }

  // Append Acuity's auto-resize embed script to <head> once.
  if (!document.querySelector(`script[src="${ACUITY_EMBED_SCRIPT}"]`)) {
    const script = document.createElement("script");
    script.src = ACUITY_EMBED_SCRIPT;
    script.async = true;
    document.head.appendChild(script);
  }
};

const initPopupBook = () => {
  const closeBtns = document.querySelectorAll<HTMLElement>(
    "[data-popup-book-close]",
  );
  const openBtns = document.querySelectorAll<HTMLElement>(
    "[data-popup-book-open]",
  );
  const drawerCloses = document.querySelectorAll<HTMLElement>(
    "[data-drawer-close]",
  );
  const header = document.getElementById("shopify-section-header");
  const popup = document.getElementById("popup-book");

  if (header && popup) {
    closeBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        header.classList.add("header__outer-wrapper");
        popup.classList.remove("is-fixed", "is-visible", "active");
        unlockBodyScroll();
      });
    });

    openBtns.forEach((btn) => {
      btn.addEventListener("click", () => {
        loadAcuityEmbed(popup);
        header.classList.remove("header__outer-wrapper");
        popup.classList.add("is-fixed", "is-visible", "active");
        if (drawerCloses.length) {
          drawerCloses.forEach((btn) => btn.click());
        }
        lockBodyScroll();
      });
    });
  }
};

document.addEventListener("DOMContentLoaded", () => {
  initPopupBook();
});
